import type { AppAction, AppState } from "./types";

export const initialState: AppState = {
  hardware: {
    cashDrawer: {
      status: "closed"
    },
    discovery: {
      printers: [],
      status: "idle"
    },
    scales: {
      discovery: {
        devices: [],
        message: null,
        status: "idle"
      },
      weight: {
        status: "unstable",
        weight: 0,
        unit: "g",
        decimals: 0
      },
      scale: null,
      model: null,
      unit: "g",
      decimals: null,
      error: null
    },
    printer: null,
    printerError: null,
    printing: {
      status: "idle",
      message: null
    },
    barcode: null,
    freedompayPoi: null,
    freedompayStatus: "unknown"
  },
  device: {
    deviceId: null,
    tillId: null,
    os: null,
    osVersion: null
  }
};

export function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "CASH_DRAWER_STATUS_INIT":
    case "CASH_DRAWER_OPEN_INIT": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          cashDrawer: {
            status: "loading"
          }
        }
      };
    }

    case "CASH_DRAWER_STATUS_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          cashDrawer: {
            status: action.payload ? "open" : "closed"
          }
        }
      };
    }

    case "CASH_DRAWER_OPEN_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          cashDrawer: {
            status: "open"
          }
        }
      };
    }

    case "CASH_DRAWER_STATUS_ERROR":
    case "CASH_DRAWER_OPEN_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          cashDrawer: {
            status: "error"
          },
          printerError: action.payload
        }
      };
    }

    case "PRINTER_DISCOVER_INIT": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          discovery: {
            ...state.hardware.discovery,
            status: "loading"
          }
        }
      };
    }

    case "PRINTER_DISCOVER_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          discovery: {
            printers: action.payload,
            status: "success"
          }
        }
      };
    }

    case "PRINTER_DISCOVER_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          discovery: {
            ...state.hardware.discovery,
            status: "error"
          }
        }
      };
    }

    case "PRINTER_DISCOVER_RESET": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          discovery: {
            ...state.hardware.discovery,
            printers: [],
            status: "idle"
          }
        }
      };
    }

    case "SCALES_DISCOVER_RESET": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            discovery: {
              devices: [],
              message: null,
              status: "idle"
            }
          }
        }
      };
    }

    case "PRINTER_SELECT_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printer: action.payload
        }
      };
    }

    case "PRINTER_DISCONNECT_SUCCESS":
    case "PRINTER_SELECT_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printer: null
        }
      };
    }

    case "DEVICE_DATA_GET_SUCCESS": {
      const { deviceId, tillId, os, osVersion, printer, scale } =
        action.payload;
      const scaleUnit = action.payload.scaleUnit ?? "g";
      const scaleDecimals = action.payload.scaleDecimals ?? null;
      const scaleModel = action.payload.model ?? null;

      return {
        ...state,
        device: {
          ...state.device,
          deviceId,
          tillId,
          os: os ?? "unknown",
          osVersion: osVersion ?? "unknown"
        },
        hardware: {
          ...state.hardware,
          printer,
          scales: {
            ...state.hardware.scales,
            error: null,
            scale,
            model: scaleModel,
            unit: scaleUnit,
            decimals: scaleDecimals
          }
        }
      };
    }

    case "PRINTER_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printerError: action.payload
        }
      };
    }

    case "PRINTER_COMMUNICATION_ERROR": {
      // The wrapper reopens from its own storage on the next print; clearing
      // `printer` here would hide every affordance that triggers one.
      // @see pos-mobile-wrapper hardware/printer/printerManager.ts openConnection
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printerError: action.payload
        }
      };
    }

    case "CLEAR_PRINTER_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printerError: null
        }
      };
    }

    case "DEVICE_ASSIGN_TILL": {
      return {
        ...state,
        device: {
          ...state.device,
          tillId: action.payload
        }
      };
    }

    case "PRINTER_PRINT_RECEIPT_INIT": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printing: {
            status: "loading",
            message: null
          }
        }
      };
    }

    case "PRINTER_PRINT_RECEIPT_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printing: {
            status: "success",
            message: null
          }
        }
      };
    }

    case "PRINTER_PRINT_RECEIPT_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printing: {
            status: "error",
            message: action.payload
          }
        }
      };
    }

    case "PRINTER_PRINT_RECEIPT_RESET": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          printing: {
            status: "idle",
            message: null
          }
        }
      };
    }

    case "BARCODE_SCANNED": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          barcode: action.payload
        }
      };
    }

    case "CLEAR_BARCODE_SCANNED": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          barcode: null
        }
      };
    }

    case "SCALE_DISCOVER_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            discovery: {
              devices: [],
              message: action.payload,
              status: "error"
            }
          }
        }
      };
    }

    case "SCALE_DISCOVER_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            discovery: {
              devices: action.payload,
              message: null,
              status: "success"
            }
          }
        }
      };
    }

    case "SCALE_SELECT_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            scale: action.payload,
            error: null
          }
        }
      };
    }

    case "SCALE_REMOVE_SUCCESS": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            scale: null,
            model: null,
            unit: "g",
            decimals: null,
            weight: initialState.hardware.scales.weight,
            error: null
          }
        }
      };
    }

    case "SCALE_SELECT_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            scale: null,
            model: null,
            unit: "g",
            decimals: null,
            error: action.payload
          }
        }
      };
    }

    case "SCALE_CONNECT_ERROR": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            error: action.payload
          }
        }
      };
    }

    case "SCALE_WEIGHT_UPDATE": {
      // A reading that parsed is proof the scale is readable again
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            error: null,
            weight: {
              status: action.payload.status,
              weight: action.payload.weight,
              unit: action.payload.unit ?? "g",
              decimals: action.payload.decimals ?? 0
            }
          }
        }
      };
    }

    case "SCALE_PAIRING_SELECTED": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            model: action.payload.model,
            unit: action.payload.unit,
            decimals: action.payload.decimals
          }
        }
      };
    }

    case "SCALE_WEIGHT_ERROR": {
      // Also drop the last reading: keeping a stale stable weight would let
      // the till book the previous item while the scale is unreadable.
      return {
        ...state,
        hardware: {
          ...state.hardware,
          scales: {
            ...state.hardware.scales,
            weight: initialState.hardware.scales.weight,
            error: action.payload
          }
        }
      };
    }
    case "FREEDOMPAY_FCC_STATUS_UPDATE": {
      return {
        ...state,
        hardware: {
          ...state.hardware,
          freedompayStatus: action.payload.status
        }
      };
    }
    case "FREEDOMPAY_POI_STATUS_UPDATE": {
      if (action.payload.status === "connected") {
        return {
          ...state,
          hardware: {
            ...state.hardware,
            freedompayPoi: action.payload.poi
          }
        };
      }

      if (action.payload.status === "disconnected") {
        return {
          ...state,
          hardware: {
            ...state.hardware,
            freedompayPoi: null
          }
        };
      }

      return state;
    }

    default: {
      return state;
    }
  }
}
